import moment = require("moment");
import {ComponentRating} from "../../model/ComponentRating";
import {Manager} from "../../model/manager/Manager.model";
import {OperationalDueDiligenceRatings, ProductOddRating} from "../../model/product/OperationalDueDiligenceRatings";
import {Product, Rating} from "../../model/product/Product.model";
import {ProductSummary} from "../../model/product/ProductSummary";
import {Ticker} from "../../model/product/Ticker";
import {NOT_RATED, NR_RATING} from "../../utils/ratingConstants";
import {AssetClassMapper} from "../research/research-filters/FilterAndSort.menuItems";

export const mapProductSummary = (productSummaryData: any) => {
    return new ProductSummary(
        productSummaryData.ratingType,
        productSummaryData.backstopId,
        productSummaryData.name,
        new Manager(productSummaryData.managerBackstopId, productSummaryData.managerName),
        productSummaryData.currentRating,
        parseDate(productSummaryData.currentRatingEffectiveDate, productSummaryData.backstopId),
        AssetClassMapper[productSummaryData.assetType],
        productSummaryData.nameForSearch,
        productSummaryData.tickers.map(mapTicker),
        productSummaryData.inPortfolio,
        productSummaryData.region,
        productSummaryData.category.replace(/(.*)\.\s/, ""),
        productSummaryData.style.replace(/(.*)\.\s/, ""),
        productSummaryData.approach,
        getIfaRating(productSummaryData.ifaRating),
        getOddRating(productSummaryData.oddRating),
        getProductRating(productSummaryData.productRating, productSummaryData.ifaRating),
    );
};

export const IFA_ONLY_RATING = "IFA Only Rating";

export const getProductRating = (productRating: string, ifaRating: string | undefined): string => {
    const productRatingTransformed = productRating === IFA_ONLY_RATING
        ? ifaRating
            ? `Inform - ${getIfaRating(ifaRating)}` : ""
        : productRating;
    return productRatingTransformed === "" || productRatingTransformed === "Inform - Not Rated"
        ? NOT_RATED
        : productRatingTransformed;
};

const getIfaRating = (ifaRating: string): string => {
    return ifaRating === "---" ? NOT_RATED : ifaRating;
};

function parseDate(dateToParse: string, backstopId: number): Date {
    const momentDate = moment(dateToParse);
    if (!momentDate.isValid()) {
        throw new Error(`Unparsable date ${dateToParse} for Product with id ${backstopId}`);
    }

    return momentDate.toDate();
}

function getOddRating(oddRatingJson: any): ProductOddRating {
    return oddRatingJson
        ? new ProductOddRating(oddRatingJson.rating, oddRatingJson.date)
        : new ProductOddRating(NR_RATING, null);
}

const mapTicker = (tickerData: { name: string, shareClass: string }) => {
    return new Ticker(tickerData.name, tickerData.shareClass);
};

export const mapProduct = (productResponse: any): Product => {
    const businessRating = new ComponentRating(
        productResponse.businessRating.rating,
        productResponse.businessRating.comments,
        productResponse.businessRating.ratingPrevious,
        productResponse.businessRating.movement,
    );
    const staffRating = new ComponentRating(
        productResponse.staffRating.rating,
        productResponse.staffRating.comments,
        productResponse.staffRating.ratingPrevious,
        productResponse.staffRating.movement,
    );
    const processRating = new ComponentRating(
        productResponse.processRating.rating,
        productResponse.processRating.comments,
        productResponse.processRating.ratingPrevious,
        productResponse.processRating.movement,
    );
    const riskRating = new ComponentRating(
        productResponse.riskRating.rating,
        productResponse.riskRating.comments,
        productResponse.riskRating.ratingPrevious,
        productResponse.riskRating.movement,
    );
    const performanceAnalysisRating = new ComponentRating(
        productResponse.performanceAnalysisRating.rating,
        productResponse.performanceAnalysisRating.comments,
        productResponse.performanceAnalysisRating.ratingPrevious,
        productResponse.performanceAnalysisRating.movement,
    );
    const termsAndConditionsRating = new ComponentRating(
        productResponse.tncRating.rating,
        productResponse.tncRating.comments,
        productResponse.tncRating.ratingPrevious,
        productResponse.tncRating.movement,
    );
    const esgRating = productResponse.esgRating
        ? new ComponentRating(
            productResponse.esgRating.rating,
            productResponse.esgRating.comments,
            productResponse.esgRating.ratingPrevious,
            productResponse.esgRating.movement,
        )
        : null;
    const currentRating = new Rating(
        productResponse.currentRating.value,
        productResponse.currentRating.date,
        productResponse.currentRating.ratingType,
    );
    const previousRating = productResponse.previousRating && new Rating(
        productResponse.previousRating.value,
        productResponse.previousRating.date,
        productResponse.previousRating.ratingType,
    );
    return new Product(
        productResponse.backstopId,
        productResponse.name,
        productResponse.managerBackstopId,
        productResponse.managerName,
        businessRating,
        staffRating,
        processRating,
        riskRating,
        performanceAnalysisRating,
        termsAndConditionsRating,
        esgRating,
        currentRating,
        previousRating,
        productResponse.overallRatingComments,
        mapOperationalDueDiligence(productResponse.oppDueDiligence),
        productResponse.monitoringPoints,
        productResponse.majorDevelopments,
        productResponse.leadResearcher,
        productResponse.backupResearcher,
        );
};

const mapOperationalDueDiligence = (oppDueDiligenceRatings: any) => {
    return oppDueDiligenceRatings && new OperationalDueDiligenceRatings(
        oppDueDiligenceRatings.rating,
        oppDueDiligenceRatings.ratingComments,
        oppDueDiligenceRatings.ratingPrevious,
        oppDueDiligenceRatings.ratingEffectiveDate,
        oppDueDiligenceRatings.previousRatingEffectiveDate,
    );
};
